import { CreditCardIcon } from '@heroicons/react/24/outline';
import { Form } from '@remix-run/react';
import { GQLBaseOrderFragment, GQLOrderFragment, GQLOrderStatusType, GQLStoreFragment } from '@theprintbar/graphql';
import {
	PartnerRoutes,
	capitalise,
	cn,
	getOrderPayment,
	getOrderQuantity,
	getReadableDateTime,
	getReadableTime,
	isOrderFragment,
	orderCanAddItems,
	orderHasCompleted,
	sortedOrderStatuses,
	useCountdown,
} from '@theprintbar/shared';
import {
	ActionToast,
	AddressDetails,
	Button,
	ButtonSize,
	ButtonVariant,
	Card,
	LoadingModal,
	ModalOutlet,
	OrderPayment,
	OrderPricing,
	OrderStatusBar,
	PageContainer,
	PrimaryLinkButton,
} from '@theprintbar/ui';
import { ReactNode } from 'react';
import { ActivityLog } from './components/ActivityLog';
import { CustomerDetails } from './components/CustomerDetails';
import { OrderLineItems } from './components/OrderLineItems';
import { OrderNotes } from './components/OrderNotes';
import { OrderPills } from './components/OrderPills';

interface OrderPageProps {
	backLink: string;
	backText?: string;
	order: GQLOrderFragment | GQLBaseOrderFragment;
	orderRoute: string;
	store?: GQLStoreFragment;
	staff?: boolean;
	actions?: ReactNode;
	webDomain?: string;
	warehouseURL?: string;
	refundReceiptLink?: (id: string) => string;
	generateItemLink?: (id: string) => string;
	viewInvoiceLink?: string;
	hideLinks?: boolean;
	hiddenContent?: {
		title: string;
		description: string;
		link?: string;
		linkText?: string;
	};
	pushDate?: string | null;
}

export function OrderPage({
	backLink,
	backText,
	order,
	webDomain,
	orderRoute,
	warehouseURL,
	refundReceiptLink,
	generateItemLink,
	viewInvoiceLink,
	store,
	staff,
	actions,
	hiddenContent,
	pushDate,
}: OrderPageProps) {
	const payment = getOrderPayment(order);
	const { countdown } = useCountdown(pushDate ? (new Date(pushDate).getTime() - Date.now()) / 1000 : 0);

	const countDownMinutes = Math.ceil((countdown % 3600) / 60);
	const countDownSeconds = Math.ceil(countdown % 60);

	return (
		<>
			{!!pushDate && (
				<div className="bg-orange border border-orange text-sm text-center py-1 relative">
					<h5 className="uppercase">Order will be pushed to the warehouse {countdown < 3600 ? 'in' : 'at'}:</h5>
					<p className="pt-1">
						{countdown < 3600
							? `${countDownMinutes} minute${countDownMinutes > 1 ? 's' : ''} and ${countDownSeconds} second${
									countDownSeconds > 1 ? 's' : ''
							  }`
							: getReadableDateTime(pushDate)}
					</p>
					<Form method="post" reloadDocument className="absolute right-2 inset-y-0 flex items-center">
						<Button size={ButtonSize.S} variant={ButtonVariant.error} type="submit" name="action" value="clearWarehousePush">
							Cancel push
						</Button>
					</Form>
				</div>
			)}
			<PageContainer
				limitWidth
				title={
					<span className="flex items-start gap-2">
						Order #{order.id} <OrderPills order={order} warehouseURL={warehouseURL} store={store} />
					</span>
				}
				subtitle={
					<>
						Placed {getReadableDateTime(order.createdAt)}
						{!!staff && (
							<>
								<br /> <b>Total units: {getOrderQuantity(order)}</b>
							</>
						)}
					</>
				}
				back={{
					to: backLink,
					label: backText || 'Back to orders',
				}}
				actions={actions}
			>
				<div className="space-y-8">
					<ModalOutlet context={{ order }} />
					<ActionToast />
					<LoadingModal onlyOnRoute={orderRoute} />
					{!!store && order.status === GQLOrderStatusType.PendingPayment ? (
						<Card className="bg-orange/80 flex justify-between items-center">
							<div className="text-sm">
								<h5 className="uppercase">Payment Pending</h5>
								<p>For this order to begin production you must trigger a payment</p>
							</div>
							<div className="flex items-center space-x-4">
								<PrimaryLinkButton icon={CreditCardIcon} to={PartnerRoutes.Store(store.id).Billing.Landing}>
									Pay for pending orders
								</PrimaryLinkButton>
							</div>
						</Card>
					) : (
						<OrderStatusBar order={order} />
					)}

					{isOrderFragment(order) ? (
						<>
							<div className={cn('grid gap-8', staff ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-2')}>
								{staff && <CustomerDetails staff order={order} billing={payment?.billingAddress} webDomain={webDomain} />}
								{order.pickupLocation ? (
									<AddressDetails address={order.pickupLocation} title="Pickup Location" />
								) : (
									<div className="text-xs lg:text-sm">
										<h5 className="mb-2 uppercase">Shipping Method</h5>
										<p className="mb-4">{capitalise(order.shippingMethod)}</p>
										<AddressDetails
											editLink={staff && !orderHasCompleted(order) ? './shipping' : ''}
											address={order.shippingAddress}
											title="Shipping Address"
										/>
									</div>
								)}
								<AddressDetails
									editLink={staff && !orderHasCompleted(order) ? './billing' : ''}
									address={payment?.billingAddress}
									title="Billing Address"
								/>
							</div>

							<div>
								<OrderLineItems
									staff={staff}
									canAddItems={orderCanAddItems(order)}
									tracking={order.tracking}
									orderShipmentId={order.shipmentId}
									generateLink={generateItemLink}
									items={order.items}
									webDomain={webDomain}
									warehouseURL={warehouseURL}
								/>
							</div>
							<div className="grid grid-cols-2 gap-4 lg:gap-8">
								<OrderPayment
									staff={staff}
									webDomain={webDomain}
									viewInvoiceLink={viewInvoiceLink}
									order={order}
									refundReceiptLink={refundReceiptLink}
								/>
								<div className="ml-auto">
									<OrderPricing order={order} staff={staff} />
									{/* {staff && <p>{order.payment?.linesTotal}</p>} */}
								</div>
							</div>
							<OrderNotes order={order} staff={staff} />
							{staff && <ActivityLog staff={staff} order={order} />}
						</>
					) : (
						<div className="border space-y-2 border-blue bg-orange/20 rounded-3xl px-4 py-4">
							<h2>{hiddenContent?.title || 'Order details have been hidden'}</h2>
							<p>
								{hiddenContent?.description ||
									'This order has been hidden from view for security. If you believe this is an error, please contact us.'}
							</p>
							{hiddenContent?.link && (
								<PrimaryLinkButton to={hiddenContent?.link} className="bg-orange text-blue mt-4">
									{hiddenContent?.linkText || 'More details'}
								</PrimaryLinkButton>
							)}
						</div>
					)}
				</div>
			</PageContainer>
		</>
	);
}
